import React, { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "react-toastify";
import Select from "react-select";
import { useNavigate } from "react-router-dom";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

function AddReceivedStockOnLoan({ dispatchedStockOnLoanCode }) {
  const [incomingStockOnLoan, setIncomingStockOnLoan] = useState([]);
  const [loading, setLoading] = useState(true);
  const [fromLocation, setFromLocation] = useState("");
  const [fromLocations, setFromLocations] = useState([]);
  const [toLocations, setToLocations] = useState([]);
  const [items, setItems] = useState([]);
  const [transferOrderDate, setTransferOrderDate] = useState(new Date().toISOString().split("T")[0]); 
  const [transferItems, setTransferItems] = useState([{ product_id: "", qty: 1, selected: true }]);
  const [products, setProducts] = useState([]);
  const [currentUser, setCurrentUser] = useState("");
  const [availableStock, setAvailableStock] = useState([]); 
  const [receivedMap, setreceivedMap] = useState({});
  const [productMappings, setProductMappings] = useState([]);

  const navigate = useNavigate();

  useEffect(() => {
    const fetchCurrentUser = async () => {
      const token = localStorage.getItem("token");
      if (!token) {
        toast.error("Authentication token missing. Please log in.");
        return;
      }

      try {
        const response = await axios.get(
          `${API_BASE_URL}/users/specific`,
          {
            headers: { Authorization: `Bearer ${token}` },
          }
        );

        const userData = response.data;
        setCurrentUser(userData);

        if (userData.role === "Main Admin") {
          fetchAllLocations();
          fetchAllProducts();
        } else if (userData.role === "Company Admin") {
          fetchFilteredLocations(userData);
          fetchAllProducts();
        }else if (userData.role === "Farm User") {
          fetchFilteredLocations(userData);
          fetchAllProducts();
        }
      } catch (error) {
        console.error("Error fetching user data:", error);
        toast.error("Failed to fetch user data.");
      }
    };

    fetchCurrentUser();

    const fetchStockOnLoan = async () => {
      if (!dispatchedStockOnLoanCode) {
        console.error("Missing dispatched stock on loan code!");
        toast.error("Dispatched Stock On Loan Code is missing!");
        return;
      }
    
      const token = localStorage.getItem("token");
      if (!token) {
        console.error("Authentication token is missing!");
        toast.error("Please log in again.");
        return;
      }
    
      try {
        const dispatchResponse = await axios.get(
          `${API_BASE_URL}/dispatchedStockOnLoan/code/${dispatchedStockOnLoanCode}`,
          {
            headers: { Authorization: `Bearer ${token}` }, 
          }
        );
    
        const receivedRes = await axios.get(
          `${API_BASE_URL}/receivedStockOnLoan/bydispatchcode/code/${dispatchedStockOnLoanCode}`,
          {
            headers: { Authorization: `Bearer ${token}` },
          }
        );

        const resultItems = receivedRes.data?.data || [];

        const stockData = dispatchResponse.data?.dispatchedStockOnLoan?.[0];
        setIncomingStockOnLoan(dispatchResponse.data);

        if (stockData?.from_location_id) {
          setFromLocation(
            typeof stockData.to_location_id === "string"
              ? stockData.to_location_id
              : stockData.to_location_id._id
          );
        }

        const dispatchedItems = stockData?.dispatchedStockItems || [];

        const validDispatchedItems = resultItems
          .filter((item) => item.remainingQty > 0)
          .map((item) => {
            const fullProduct = stockData?.dispatchedStockItems.find(
              (di) => di._id.toString() === item.dispatched_on_loan_id
            )?.product_id;

            return {
              dispatchedItemId: item.dispatched_on_loan_id,
              product_id: fullProduct?._id,
              qty: item.remainingQty,
              maxQty: item.remainingQty,
              disabled: item.remainingQty <= 0,
              unit: fullProduct?.unit?.unit_name || "",
              dispatched_qty: item.dispatchedQty,
              already_received_qty: item.receivedQty,
              product_code: fullProduct?.product_code,
              product_name: fullProduct?.product_name,
            };
          });

        setTransferItems(validDispatchedItems);

      } catch (error) {
        console.error("Error fetching stock on loan details:", error);

        if (error.response?.status === 401) {
          toast.error("Unauthorized. Please log in again.");
        } else {
          toast.error("Failed to fetch stock on loan details.");
        }
      } finally {
        setLoading(false);
      }
    };

        fetchStockOnLoan();
  }, [dispatchedStockOnLoanCode]);

  const idOf = (val) => {
    if (!val) return null;
    if (typeof val === "string") return val;
    if (val._id) return val._id;
    if (val.$oid) return val.$oid;
    return null;
  };

  const fetchProductMappings = async () => {
    // only fetch when we know cluster/toOrg/fromOrg
    const dispatched = incomingStockOnLoan?.dispatchedStockOnLoan?.[0];
    const clusterId = idOf(dispatched?.cluster_id);
    const toOrgId = idOf(dispatched?.to_organization_id);
    const fromOrgId = idOf(currentUser?.organization?._id || currentUser?.organization);

    if (!clusterId || !fromOrgId) {
      // nothing to fetch yet, clear or keep existing
      return;
    }

    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(`${API_BASE_URL}/productMappings`, {
        headers: { Authorization: `Bearer ${token}` },
        // try passing server-side filters if supported; if not, server should ignore unknown params
        params: { clusterId, fromOrganization: fromOrgId, toOrganization: toOrgId },
      });
      console.log("Fetched product mappings:", response.data);

      // API may return response.data.data or response.data; be defensive
      const data = response.data?.data ?? response.data ?? [];
      setProductMappings(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error("Failed to fetch product mappings:", err);
      // don't block UX; show a toast optionally
      // toast.error("Could not fetch product mappings.");
    }
  };

  useEffect(() => {
    // re-fetch whenever the dispatched record or currentUser changes
    fetchProductMappings();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUser, incomingStockOnLoan]);


  const fetchAllLocations = async () => {
    const token = localStorage.getItem("token");
    const response = await axios.get(
      `${API_BASE_URL}/locations`,
      {
        headers: { Authorization: `Bearer ${token}` },
      }
    );
    if (currentUser.role === "Farm User") {
      setFromLocations(response.data.fromLocations || []); 
      setToLocations(response.data.toLocations || []); 
    } else {
      setFromLocations(response.data.fromLocations); 
      setToLocations(response.data.toLocations);
    }
  };

  const fetchAllProducts = async () => {
    const token = localStorage.getItem("token");
    const response = await axios.get(
      `${API_BASE_URL}/products`,
      {
        headers: { Authorization: `Bearer ${token}` },
      }
    );
    setProducts(response.data.products);
  };

  const fetchFilteredLocations = async () => {
    const token = localStorage.getItem("token");
    const response = await axios.get(
      `${API_BASE_URL}/products/filtered-to-from-locations`,
      {
        headers: { Authorization: `Bearer ${token}` },
      }
    );

    if (currentUser.role === "Farm User") {
      setFromLocations(response.data.fromLocations || []); 
      setToLocations(response.data.toLocations || []);
    } else {
      setFromLocations(response.data.fromLocations); 
      setToLocations(response.data.toLocations);
    }
  };

  useEffect(() => {
    console.log(products);
  }, [products]);

  const handleTransferItemChange = (index, field, value) => {
    const updatedItems = [...transferItems];

    if (field === "product_id") {
      if (!value) {
        updatedItems[index] = {
          ...updatedItems[index],
          product_id: "",
          qty: 0,
          unit: "",
        };
      } else {
        const selectedProduct = products.find((p) => p._id === value);
        updatedItems[index] = {
          ...updatedItems[index],
          product_id: value,
          unit: selectedProduct?.unit?.unit_name || "",
        };
      }
    } else {
      updatedItems[index][field] = value;
    }

    setTransferItems(updatedItems);
  };
  
  const handleCheckboxChange = (index) => {
    const updatedItems = [...transferItems];
    updatedItems[index].selected = !updatedItems[index].selected;
    setTransferItems(updatedItems);
  };

  
  const ProductOption = (props) => {
    const { data, innerRef, innerProps, isFocused, isSelected } = props;
  
    return (
      <div
        ref={innerRef}
        {...innerProps}
        className={`flex items-center p-2 cursor-pointer ${
          isFocused ? "bg-gray-200" : ""
        } ${isSelected ? "bg-blue-500 text-white" : ""}`}
      >
        {data.product_image && (
          <img
            src={`${process.env.REACT_APP_IMAGE_BASE_URL}${data.product_image}`}
            alt={data.product_name}
            className="w-8 h-8 object-cover rounded mr-2"
          />
        )}
        <span>{data.product_name}</span>
      </div>
    );
  };
  
  const customProductSelect = (productsList, index) => {
  const selectedItem = transferItems[index];
  if (!selectedItem) return null;

  const dispatched = incomingStockOnLoan?.dispatchedStockOnLoan?.[0];
  const clusterId = idOf(dispatched?.cluster_id);
  const fromOrgId = idOf(currentUser?.organization?._id || currentUser?.organization);
  const toOrgId = idOf(dispatched?.to_organization_id);

  const activeProducts = (productsList || []).filter(p => p.status === "Active");

  const existingMapping = productMappings.find(
    (m) =>
      idOf(m.clusterId) === clusterId &&
      idOf(m.fromOrganization) === fromOrgId &&
      idOf(m.fromProductId) === idOf(selectedItem?.product_id)
  );

  const mappedToProductId = idOf(existingMapping?.toProductId);

  const selectedValue =
    mappedToProductId
      ? activeProducts.find((p) => idOf(p._id) === mappedToProductId) || null
      : activeProducts.find((p) => idOf(p._id) === idOf(selectedItem?.product_id)) || null;

  return (
    <Select
      options={activeProducts}
      getOptionLabel={(o) => o.product_name || o.product_code || ""}
      getOptionValue={(o) => idOf(o._id) || ""}
      components={{ Option: ProductOption }}
      value={selectedValue}
      isClearable
      placeholder="Select"
      className="w-full"
      onChange={(selectedOption) => {
        if (!selectedOption) {
          handleTransferItemChange(index, "product_id", "");
          return;
        }

        const fromProductId = idOf(transferItems[index]?.product_id);
        const toProductId = idOf(selectedOption._id);

        if (existingMapping) {
          const confirmed = window.confirm(
            "Are you sure you want to update this product mapping?"
          );
          if (!confirmed) return;

          updateProductMappingAPI(existingMapping._id, toProductId);
        } else {
          createProductMappingAPI(fromProductId, toProductId);
        }

        handleTransferItemChange(index, "product_id", toProductId);
      }}
    />
  );
};

  const createProductMappingAPI = async (fromProductId, toProductId) => {
    const token = localStorage.getItem("token");
    const dispatched = incomingStockOnLoan?.dispatchedStockOnLoan?.[0];
    if (!dispatched || !fromProductId || !toProductId) return;

    const clusterId = idOf(dispatched?.cluster_id);
    const fromOrgId = idOf(currentUser?.organization?._id || currentUser?.organization);
    const toOrgId = idOf(dispatched?.to_organization_id);

    const already = productMappings.find((m) =>
      idOf(m.clusterId) === clusterId &&
      idOf(m.fromOrganization) === fromOrgId &&
      idOf(m.fromProductId) === fromProductId
    );
    if (already) return updateProductMappingAPI(already._id, toProductId);

    const fromProductUnit = products.find(p => idOf(p._id) === fromProductId)?.unit?._id 
                          || products.find(p => idOf(p._id) === fromProductId)?.unit;
    const toProductUnit = products.find(p => idOf(p._id) === toProductId)?.unit?._id 
                        || products.find(p => idOf(p._id) === toProductId)?.unit;

    const payload = {
      clusterId,
      fromOrganization: fromOrgId,
      toOrganization: toOrgId,
      fromProductId,
      toProductId,
      fromProductUnit,
      toProductUnit
    };

    try {
      const res = await axios.post(`${API_BASE_URL}/productMappings`, payload, {
        headers: { Authorization: `Bearer ${token}` },
      });

      const created = res.data?.data ?? res.data ?? null;
      if (created) {
        setProductMappings(prev => [...prev, created]);
      }
    } catch (err) {
      console.error("Product mapping creation failed:", err.response?.data || err);
      toast.error(err.response?.data?.message || "Failed to create product mapping.");
    }
  };


  const updateProductMappingAPI = async (mappingId, newToProductId) => {
    const token = localStorage.getItem("token");
    if (!mappingId || !newToProductId) return;

    // pick potential unit id for newToProduct (optional)
    const toProductUnit = products.find(p => idOf(p._id) === newToProductId)?.unit?._id || products.find(p => idOf(p._id) === newToProductId)?.unit || undefined;

    const payload = {
      toProductId: newToProductId,
      ...(toProductUnit ? { toProductUnit } : {}),
    };

    try {
      const res = await axios.put(`${API_BASE_URL}/productMappings/${mappingId}`, payload, {
        headers: { Authorization: `Bearer ${token}` },
      });

      const updated = res.data?.data ?? res.data ?? null;
      if (updated) {
        setProductMappings(prev => prev.map(m => (idOf(m._id) === idOf(mappingId) ? updated : m)));
      } else {
        // if API returns no data, refresh from server
        fetchProductMappings();
      }
      toast.success("Product mapping updated.");
    } catch (err) {
      console.error("Failed to update mapping:", err.response?.data || err);
      toast.error(err.response?.data?.message || "Failed to update product mapping.");
    }
  };

const handleSubmit = async () => {
  if (!fromLocation) {
    toast.error("Please select valid locations.");
    return;
  }

  const selectedItems = transferItems.filter(item => item.selected);

  if (selectedItems.length === 0) {
    toast.error("Please select at least one product to submit.");
    return;
  }

  for (const item of selectedItems) {
    if (!item.product_id && item.qty > 0) {
      toast.error("Please select a product for the quantity entered.");
      return;
    }
    if (item.product_id && (!item.qty || item.qty <= 0)) {
      toast.error(`Please enter quantity greater than zero for the selected product.`);
      return;
    }
  }

  const validItems = selectedItems.filter(item => item.product_id && item.qty > 0);

  if (validItems.length === 0) {
    toast.error("Please select at least one product with valid quantity.");
    return;
  }

  const totalQty = validItems.reduce((sum, item) => sum + item.qty, 0);

  for (const item of validItems) {
    const product = products.find((prod) => prod._id === item.product_id);

    if (product) {
      const isPartialQuantityAllowed = product.partial_qty_allocation;

      if (!isPartialQuantityAllowed && !Number.isInteger(item.qty)) {
        toast.error(`Quantity for ${product.product_name} must be a whole number.`);
        return;
      }
    }

    const dispatchedItem = incomingStockOnLoan?.dispatchedStockOnLoan?.[0]?.dispatchedStockItems
      ?.find(di => di.product_id?._id === item.product_id || di.product_id === item.product_id);

    if (dispatchedItem && item.qty > dispatchedItem.qty) {
      const productName = products.find(p => p._id === item.product_id)?.product_name || "a product";
      toast.error(`Received quantity for ${productName} cannot exceed dispatched quantity (${dispatchedItem.qty}).`);
      return;
    }
  }

  const payload = {
    source_id: incomingStockOnLoan?.dispatchedStockOnLoan?.[0]?._id,
    location_id: fromLocation,
    grn_date: transferOrderDate,
    items: validItems.map(item => {
      const mapping = productMappings.find(
        (m) =>
          idOf(m.fromProductId) === idOf(item.product_id) &&
          idOf(m.clusterId) === idOf(incomingStockOnLoan?.dispatchedStockOnLoan?.[0]?.cluster_id) &&
          idOf(m.fromOrganization) === idOf(currentUser?.organization?._id || currentUser?.organization)
      );

      const toProductId = mapping ? idOf(mapping.toProductId) : idOf(item.product_id);

      return {
        product_id: toProductId, 
        received_quantity: item.qty,
        unit_price: 0,
        total_price: 0,
      };
    }),
    total_amount: totalQty,
    organization: currentUser?.organization?._id,
    created_by: currentUser._id,
    role: currentUser.role,
  };

  try {
    const token = localStorage.getItem("token");
    if (!token) {
      toast.error("Authentication token is missing. Please log in again.");
      return;
    }

    const response = await axios.post(`${API_BASE_URL}/grn/addReceipt`, payload, {
      headers: { Authorization: `Bearer ${token}` },
    });

    toast.success(response.data.message || "Stock on Loan receipt successfully!");
    console.log("Stock on Loan receipt created: ", response.data.transferOrder);

    const receivedStockPayload = {
      dispatched_stock_on_loan_code: incomingStockOnLoan?.dispatchedStockOnLoan?.[0]?.dispatched_stock_on_loan_code || "",
      from_location_id: typeof fromLocation === "string" ? fromLocation : fromLocation?._id || "",
      to_location_id:
        typeof incomingStockOnLoan?.dispatchedStockOnLoan?.[0]?.to_location_id === "string"
          ? incomingStockOnLoan?.dispatchedStockOnLoan?.[0]?.to_location_id
          : incomingStockOnLoan?.dispatchedStockOnLoan?.[0]?.to_location_id?._id || "",
      from_organization_id:
        typeof currentUser?.organization?._id === "string"
          ? currentUser.organization._id
          : currentUser.organization?._id || "",
      created_by: currentUser._id || "",
      organization: currentUser?.organization?._id || "",
      status: "Draft",
      receivedStockItems: validItems.map(item => ({
        product_id: typeof item.product_id === "string" ? item.product_id : item.product_id?._id || "",
        qty: item.qty,
      })),
    };

    console.log("Payload sent to /receivedStockOnLoan/add:", receivedStockPayload);

    const receiveResponse = await axios.post(
      `${API_BASE_URL}/receivedStockOnLoan/add`,
      receivedStockPayload,
      {
        headers: { Authorization: `Bearer ${token}` },
      }
    );

    toast.success(receiveResponse.data.message || "Received Stock On Loan recorded successfully!");
    console.log("Received Stock On Loan created:", receiveResponse.data);

    const dispatchedCode = incomingStockOnLoan?.dispatchedStockOnLoan?.[0]?.dispatched_stock_on_loan_code;

    if (dispatchedCode) {
      try {
        const headers = { headers: { Authorization: `Bearer ${token}` } };

        const receivedItemsRes = await axios.get(
          `${API_BASE_URL}/receivedStockOnLoan/bydispatchcode/code/${dispatchedCode}`,
          headers
        );

        const perDispatchItems = receivedItemsRes.data?.data || [];

        const isFullyReceived = perDispatchItems.length > 0 && perDispatchItems.every(i => (i.remainingQty <= 0));

        const statusToUpdate = isFullyReceived ? "Fully Received" : "Partially Received";

        await axios.put(
          `${API_BASE_URL}/dispatchedStockOnLoan/updateStatus/${dispatchedCode}`,
          { status: statusToUpdate },
          headers
        );

        toast.success(`Dispatch status updated to "${statusToUpdate}"`);

        setTransferItems([]);
        setFromLocation("");
        setTransferOrderDate(new Date().toISOString().split("T")[0]);
      } catch (err) {
        console.error("Error updating dispatched status:", err);
        toast.warning("Could not update dispatched status automatically. Please refresh or try again.");
      }

      try {
        const latest = await axios.get(
          `${API_BASE_URL}/receivedStockOnLoan/bydispatchcode/code/${dispatchedCode}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );

        const latestMap = {};
        (latest.data.data || []).forEach(item => {
          latestMap[item.dispatched_on_loan_id] = {
            dispatchedQty: item.dispatchedQty,
            receivedQty: item.receivedQty,
            remainingQty: item.remainingQty
          };
        });

        setTransferItems(prev =>
          prev.map(it => {
            const d = latestMap[it.dispatchedItemId];
            return d ? { ...it, maxQty: d.remainingQty, already_received_qty: d.receivedQty } : it;
          })
        );
      } catch (err) {
        console.warn("Could not refresh remaining quantities:", err);
      }
    }
  } catch (error) {
    console.error("Error submitting stock receipt:", error);
    toast.error("Failed to submit received stock. Please try again.");
  }
};

  return (
    <div className="container mx-auto p-6">
      <div className="bg-white rounded-lg shadow-lg p-6 mt-4">
        <h2 className="text-2xl font-bold text-blue-700 mb-6">Receive Stock On Loan </h2>
        
        <div className="mb-6">
          <h3 className="text-lg font-semibold text-gray-700 mb-4">Stock Items</h3>

          <div className="grid grid-cols-8 gap-4 font-semibold text-gray-800 mb-2">
            <div >Select</div>
            <div >Code</div>
            <div className="text-center">Product Name</div>
            <div className="text-right">Dispatched Qty</div>
            <div className="text-center">Unit</div>
            <div >Select Product</div>
            <div className="text-right">Received Qty</div>
            <div className="text-center">Unit</div>
          </div>

          {transferItems.map((item, index) => (
            <div key={index} className="grid grid-cols-8 gap-4 items-center mb-4">
              <div className="justify-center">
                <input
                  type="checkbox"
                  checked={item.selected || false}
                  disabled={item.disabled}
                  onChange={() => handleCheckboxChange(index)}
                  className="cursor-pointer"
                />
              </div>

              <div className="text-gray-700">{item.product_code}</div>

              <div className="text-gray-700 text-center">{item.product_name}</div>

              <div className="text-gray-700 text-right">{item.dispatched_qty}</div>

              <div className="text-gray-700 text-center">{item.unit}</div>

              <div className="w-full">
                {customProductSelect(products, index)}
              </div>

              <input
                type="number"
                disabled={!item.product_id || item.disabled || !item.selected} // also disable if not selected
                placeholder="Qty"
                className={`p-2 border rounded-lg w-full text-center ${
                  !item.product_id || item.disabled || !item.selected ? "bg-gray-100 cursor-not-allowed" : ""
                }`}
                value={item.qty}
                onChange={(e) => {
                  let val = Number(e.target.value);
                  if (val > item.maxQty) val = item.maxQty;
                  if (val < 0) val = 0;
                  handleTransferItemChange(index, "qty", val);
                }}
                min="0"
                max={item.maxQty}
                step={
                  products.find((p) => p._id === item.product_id)?.partial_qty_allocation
                    ? "0.25"
                    : "1"
                }
              />

              <div className="text-gray-700 text-center">{item.product_id ? item.unit : ""}</div>
            </div>
          ))}

          <div className="mb-6 w-fit">
                <label htmlFor="location" className="block text-sm font-medium text-gray-700 mb-1">
                  Select Location
                </label>
                <select
                  id="location"
                  value={fromLocation}
                  onChange={(e) => setFromLocation(e.target.value)}
                  className="p-2 border border-gray-300 rounded-md w-full"
                >
                  <option value="">Select Location</option>
                  {fromLocations.map((loc) => (
                    <option key={loc._id} value={loc._id}>
                      {loc.location_name}
                    </option>
                  ))}
                </select>
              </div>
        </div>
        <div className="text-right">
          <button
            onClick={handleSubmit}
            className="bg-blue-700 text-white py-3 px-6 rounded-lg hover:bg-blue-600"
          >
            Submit Receipt
          </button>
        </div>
      </div>
    </div>
  );
}

export default AddReceivedStockOnLoan;